import { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { EstimateOrderResponseDtoV1ShippingsItem } from '@/apis/rest/generated/types'
import { ImageAsset } from '@/components/common'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Typo, TypoVariant } from '@/components/common/typography'
import { OrderProductCard } from '@/components/domains/order2/order-product-card'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { IconSizeEnum } from '@/constants/icon-size.enum'
import { currencyText } from '@/utils/utilNumber'

interface ProductListSectionProps {
  shippings: EstimateOrderResponseDtoV1ShippingsItem[]
  countOfOrderItem: number
}

export const ProductListSection = ({ shippings, countOfOrderItem }: ProductListSectionProps) => {
  const { color } = useTheme()

  const [isOpen, setIsOpen] = useState<boolean>(false)

  const handleToggle = () => {
    setIsOpen((prevState) => !prevState)
  }

  return (
    <>
      <StyledSectionTitle onClick={handleToggle}>
        <Typo variant="Body1 Bold">{`주문 상품 총 ${countOfOrderItem}개`}</Typo>
        <FlatIcon type={isOpen ? IconNameEnum.IcArrowUp : IconNameEnum.IcArrowDown} size={IconSizeEnum.Size24} />
      </StyledSectionTitle>
      {isOpen && (
        <StyledProductListContainer>
          {shippings.map((shipping, index) => (
            <StyledProductList key={shipping.shippingPresetId}>
              <StyledProductListTitle>
                <Typo variant="Body3 Bold">{`${shipping.sellerShopName} (${shipping.quantityOfShippingOrderItem})`}</Typo>
              </StyledProductListTitle>

              {index > 0 && shipping.sellerShopName === shippings[index - 1].sellerShopName && (
                <StyledSeperatedShippingNotice>
                  <ImageAsset name="ic_warning_300_16" size={16} svg style={{ marginRight: '5px' }} />
                  <Typo variant="Body3 Regular" color={color.red500}>
                    출고지가 다른 분리 배송 상품입니다.
                  </Typo>
                </StyledSeperatedShippingNotice>
              )}

              {/* 상품 카드 */}
              {shipping.orderItems?.map((orderItem, index) => (
                <OrderProductCard
                  key={orderItem.productOptionId}
                  product={orderItem}
                  isLast={index === shipping.orderItems.length - 1}
                />
              ))}

              <StyledGroupTotalPrice>
                <Typo variant={TypoVariant.Heading6Bold}>{currencyText(shipping.productAmount)}</Typo>
              </StyledGroupTotalPrice>

              <StyledProductListPriceInfo>
                <StyledProductListPriceInfoRow>
                  <Typo variant="Body3 Medium" color={color.gray700}>
                    배송비
                  </Typo>
                  <Typo variant="Body3 Medium" color={color.gray700}>
                    {' '}
                    {currencyText(shipping.amount)}
                  </Typo>
                </StyledProductListPriceInfoRow>
              </StyledProductListPriceInfo>
            </StyledProductList>
          ))}
        </StyledProductListContainer>
      )}
    </>
  )
}

const StyledSectionTitle = styled.div`
  height: 4.8rem;
  padding: 1.2rem 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`

const StyledProductListContainer = styled.div`
  padding: 0 1.6rem 2rem;
`

const StyledProductList = styled.div`
  border: 1px solid ${GlobalColorEnum.Gray70};
  border-radius: 0.8rem;
  margin-bottom: 1.6rem;
`

// radius 를 0.8rem 으로 설정하면 border 와 background-color 사이에 공백이 생김
const StyledProductListTitle = styled.div`
  height: 5rem;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  padding: 1.4rem 1.6rem;
  background-color: ${GlobalColorEnum.Gray70};
  display: flex;
  align-items: center;
`

const StyledProductListPriceInfo = styled.div`
  padding: 1.6rem 1.6rem 2rem 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  border-top: 1px solid ${GlobalColorEnum.Gray70};
`

const StyledProductListPriceInfoRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.2rem;
`

const StyledSeperatedShippingNotice = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1rem 0;
`

const StyledGroupTotalPrice = styled.div`
  display: flex;
  justify-content: end;
  padding: 1.6rem;
`
